<script setup lang="ts">
import { videoMediaUrls } from '@/utils/media-urls'
import { useTopbarStore } from '@/components/app/topbar-store'
import type { IntroductionStepProps } from '@/components/cards/IntroductionProcessStep.vue'
import { useGetStatistics, useListUnhandledJobs } from '@swipe4work/api-orval'
import { useJobAgentAnnouncementDialog } from '@/composables/dialogs/useJobAgentAnnouncementDialog'

definePage({
  meta: {
    permissionResource: 'Dashboard',
    shell: {
      showLeadPromotion: false,
    },
  },
})

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const { isConferenceUser, isLead } = storeToRefs(authStore)

const { data: statistics } = useGetStatistics()
const { data: unhandledJobsData } = useListUnhandledJobs()
const unhandledJobs = computed(() => unhandledJobsData.value?.jobs || [])

const { companyComplete } = storeToRefs(useCompanyStore())
const { hasJobs } = storeToRefs(useJobStore())

const showIntroductionCard = computed(() => companyComplete.value === false || hasJobs.value === false)
const showLeadIntroduction = computed(() => statistics.value?.totalMatchCount === 0 && isLead.value)
const showsIntroduction = computed(() => (isConferenceUser.value && showIntroductionCard.value) || showLeadIntroduction.value)
const showDashboard = computed(() => statistics.value !== undefined && !showsIntroduction.value)

// Announce the rewritten create-job form, once, to employers who can actually create one.
const { openOnce: announceJobAgent } = useJobAgentAnnouncementDialog()

watch(showDashboard, (visible) => {
  if (visible && authStore.hasWritePermission('Job')) {
    announceJobAgent()
  }
}, { immediate: true })

const topbarStore = useTopbarStore()

topbarStore.setAndClear({
  breadcrumbs: [ { label: t('routes.dashboard') } ],
})

const conferenceSteps = computed((): IntroductionStepProps[] => {
  return [
    {
      isCompleted: true,
      iconClass: {
        'bi bi-play-btn-fill': true,
        '!color-surface-700': true,
      },
      onButtonClick: () => {},
      buttonLabel: 'bekijk video',
      title: 'Video bekijken',
      description: 'Leer snel en eenvoudig hoe Swipe4Work werkt',
    },
    {
      isCompleted: companyComplete.value ?? false,
      iconClass: {
        'pi pi-building': true,
        '!color-surface-700': true,
      },
      onButtonClick: () => router.push({
        name: '/company/',
        query: { next: '/jobs/create/' },
      }),
      buttonLabel: 'Aanmaken',
      title: 'Bedrijfsprofiel aanmaken',
      description: 'Maak je profiel aantrekkelijk voor sollicitanten',
    },
    {
      isCompleted: hasJobs.value ?? false,
      iconClass: {
        'bi bi-card-text': true,
        '!color-surface-700': true,
      },
      onButtonClick: () => router.push({ name: '/jobs/create' }),
      buttonLabel: 'Aanmaken',
      title: 'Zet je eerste gratis vacature live!',
      description: 'Trek gratis sollicitanten aan tijdens én na het evenement!',
    },
  ]
})

const leadSteps = computed((): IntroductionStepProps[] => {
  return [
    {
      isCompleted: true,
      iconClass: {
        'bi bi-play-btn-fill': true,
        '!color-surface-700': true,
      },
      onButtonClick: () => {},
      buttonLabel: 'bekijk video',
      title: 'Video bekijken',
      description: 'Leer snel en eenvoudig waarom Swipe4Work jouw bedrijf kan helpen',
    },
    {
      isCompleted: unhandledJobs.value.length === 0,
      iconClass: {
        'pi pi-user': true,
        '!color-surface-700': true,
      },
      onButtonClick: () => router.push({ name: '/job-applications' }),
      buttonLabel: 'Sollicitanten',
      title: 'Sollicitanten beoordelen',
      description: 'Beoordeel de openstaande sollicitanten en vind de perfecte match',
    },
  ]
})
</script>

<template>
  <!-- Dashboard is always mounted (hidden until ready) so its own queries fire immediately
       instead of waiting for the introduction-card decision above to resolve. -->
  <Dashboard v-show="showDashboard" />
  <template v-if="!showDashboard">
    <!-- only show introduction for conference user. In the future we can easily show it for all users. -->
    <IntroductionCard
      v-if="isConferenceUser && showIntroductionCard"
      :video-src="videoMediaUrls.conferenceOnboarding"
      :steps-title="t('conference.introduction.steps.title')"
      :title="t('conference.introduction.title')"
      :subtitle="t('conference.introduction.subtitle')"
      :steps="conferenceSteps"
    />
    <IntroductionCard
      v-else-if="showLeadIntroduction"
      :video-src="videoMediaUrls.leads"
      :steps-title="t('leads.introduction.steps.title')"
      :title="t('leads.introduction.title')"
      :subtitle="t('leads.introduction.subtitle')"
      :steps="leadSteps"
    />
    <div v-else>
      <Skeleton width="33#" height="10rem" />
    </div>
  </template>
</template>
